import type { AtlasManifest, Feature, FlagCatalogRow } from '@atlas-manifest/types';
import { scoreFeatureFlagPair, type MatchCandidate } from '@atlas-manifest/parser/matchFeatureFlags.js';
import type { RefactorProposal } from './refactorProposals.js';
import type { AskAtlasResult } from './atlasApiTypes.js';

export interface AiSuggestOptions {
  prompt: string;
  summary: { entityCount: number; domainCount: number; domainNames: string[] };
  knownEntityIds: string[];
}

// Re-export so callers can import from one place.
export type { AskAtlasResult };

function manifestSummary(manifest: AtlasManifest) {
  return {
    entityCount: manifest.entities.length,
    domainCount: manifest.domains.length,
    domainNames: manifest.domains.map((d) => d.name),
  };
}

function allEntityIds(manifest: AtlasManifest): string[] {
  return manifest.entities.map((e) => e.id);
}

/** Q2 — PR description draft for a refactor proposal. */
export function refactorProposalDraftPrompt(
  proposal: RefactorProposal,
  manifest: AtlasManifest,
): AiSuggestOptions {
  return {
    prompt:
      `You are a senior schema architect. Write a concise GitHub PR description ` +
      `(title + 2–3 paragraphs) for this refactor:\n\n` +
      `Proposal: ${proposal.title}\n` +
      `Kind: ${proposal.kind}\n` +
      `Entity: ${proposal.entityId}\n` +
      `Rationale: ${proposal.rationale}\n\n` +
      `Focus on the WHY and migration impact. Do not invent entity names that do not appear in the rationale.`,
    summary: manifestSummary(manifest),
    knownEntityIds: allEntityIds(manifest),
  };
}

/** Q1 — Blast-radius mitigation suggestions for a given entity. */
export function impactSuggestPrompt(
  entityId: string,
  manifest: AtlasManifest,
): AiSuggestOptions {
  const entity = manifest.entities.find((e) => e.id === entityId);
  const fieldCount = entity?.fields.length ?? 0;
  return {
    prompt:
      `You are a senior schema architect. The entity "${entityId}" (${fieldCount} fields) ` +
      `has a large blast radius. List 3 concrete mitigation strategies for reducing its coupling. ` +
      `Reference actual dependents from the schema where possible.`,
    summary: manifestSummary(manifest),
    knownEntityIds: allEntityIds(manifest),
  };
}

/** Q1 — Decoupling strategies for a TraceView source entity. */
export function traceDecouplePrompt(
  fromId: string,
  manifest: AtlasManifest,
): AiSuggestOptions {
  return {
    prompt:
      `You are a senior schema architect. "${fromId}" is the source of a deep dependency chain. ` +
      `Suggest 2–3 concrete decoupling strategies (e.g. introduce an abstraction layer, ` +
      `extract a value object, invert a dependency). Be specific to the schema.`,
    summary: manifestSummary(manifest),
    knownEntityIds: allEntityIds(manifest),
  };
}

/** Q3 — Regulatory compliance narrative for a given regime. */
export function complianceNarrativePrompt(
  regimeId: string,
  manifest: AtlasManifest,
): AiSuggestOptions {
  return {
    prompt:
      `You are a compliance engineer. Write a brief regulatory narrative (3–4 sentences) ` +
      `explaining how this schema handles ${regimeId.toUpperCase()} compliance. ` +
      `Focus on data minimisation, retention boundaries, and which entities handle PII.`,
    summary: manifestSummary(manifest),
    knownEntityIds: allEntityIds(manifest),
  };
}

/**
 * Q5 — Rank entity IDs by relevance to a search query.
 * The model must return ONLY entity IDs, one per line — `parseRankedIds` filters
 * the response to the valid candidate set before the caller uses it.
 */
export function searchRankPrompt(
  query: string,
  candidateIds: string[],
  manifest: AtlasManifest,
): AiSuggestOptions {
  return {
    prompt:
      `Rank the following entity IDs by relevance to the search query: "${query}"\n\n` +
      `Return ONLY the entity IDs, one per line, most relevant first. ` +
      `Do not add any explanation or extra text.\n\nEntity IDs:\n${candidateIds.join('\n')}`,
    summary: manifestSummary(manifest),
    knownEntityIds: candidateIds,
  };
}

/**
 * "Describe it" — turn a free-text design-change description into a single
 * proposed SandboxOp. `parseAiOpsResponse` (state/useAiSuggest.ts) validates
 * the response and drops anything that doesn't match one of these shapes
 * exactly; callers fall back to the local keyword heuristic (kindGuesser.ts)
 * when this yields no valid op — AI enhances this flow, it never gates it.
 */
export function describeDesignChangePrompt(
  description: string,
  manifest: AtlasManifest,
): AiSuggestOptions {
  return {
    prompt:
      `You are a senior schema architect helping a teammate propose a new design-change ` +
      `object for this manifest. They described what they want in plain language:\n\n` +
      `"${description}"\n\n` +
      `Reply with ONLY a JSON array containing exactly one object, matching one of these ` +
      `shapes exactly (pick the single best-fitting kind):\n\n` +
      `{"kind":"create-entity","entityId":string,"name":string,"domain":string,"description":string}\n` +
      `{"kind":"create-domain","domainId":string,"name":string,"color":string,"description":string}\n` +
      `{"kind":"create-reference","from":string,"to":string,"fromField":string,"referenceKind":"fk"|"embed"|"enum"|"aggregate"|"event"}\n` +
      `{"kind":"create-surface","surfaceId":string,"surfaceType":"card"|"chart"|"overlay"|"chip"|"ai"|"hero"|"list"|"sheet"|"banner"|"grid"|"other","domain":string,"hub":string}\n` +
      `{"kind":"create-cloud-function","name":string,"functionKind":"callable"|"http"|"trigger"|"scheduled","domain":string}\n` +
      `{"kind":"create-event","eventType":string,"category":string,"safetyTier":0|1|2|3}\n` +
      `{"kind":"create-path","entityId":string,"path":string,"retention":string}\n` +
      `{"kind":"create-tag","tag":string,"description":string}\n` +
      `{"kind":"create-state-machine","machineId":string,"name":string,"domain":string}\n\n` +
      `"domain" values must be one of the existing domain names below. Do not invent an ` +
      `entity id that already exists in the manifest unless the description clearly refers ` +
      `to that existing entity. No prose, no markdown code fences — the JSON array is the ` +
      `entire response.`,
    summary: manifestSummary(manifest),
    knownEntityIds: allEntityIds(manifest),
  };
}

export type EditableKind = 'entity' | 'surface' | 'cloud-function' | 'event-type' | 'firestore-path';

const EDIT_OP_KIND: Record<EditableKind, string> = {
  entity: 'update-entity',
  surface: 'update-surface',
  'cloud-function': 'update-cloud-function',
  'event-type': 'update-event',
  'firestore-path': 'update-path',
};

const EDIT_SELECTOR_KEY: Record<EditableKind, string> = {
  entity: 'entityId',
  surface: 'surfaceId',
  'cloud-function': 'name',
  'event-type': 'type',
  'firestore-path': 'path',
};

/** Patchable fields per kind — must match the update-* op shapes exactly (sandboxOps.ts, Task 1). */
const EDIT_PATCHABLE_FIELDS: Record<EditableKind, readonly string[]> = {
  entity: ['name', 'domain', 'description'],
  surface: ['surfaceType', 'domain', 'hub', 'description'],
  'cloud-function': ['functionKind', 'domain', 'description', 'rationale', 'triggerCondition'],
  'event-type': ['category', 'description', 'businessTrigger', 'retentionDays'],
  'firestore-path': ['retention', 'securityRuleNote', 'owner', 'quotaNotes'],
};

/**
 * "Ask Atlas to edit this" — turn a free-text instruction plus an existing
 * object into a single proposed update-* SandboxOp. Distinct from
 * describeDesignChangePrompt (which proposes brand-new objects): this always
 * targets one specific existing object, and the prompt explicitly excludes
 * the identity-key selector from the patchable-field list so the model is
 * never invited to attempt a rename (sandboxOps.ts's identity-key
 * immutability principle — Task 1).
 */
export function proposeObjectEditPrompt(
  kind: EditableKind,
  selectorId: string,
  currentObject: Record<string, unknown>,
  instruction: string,
  manifest: AtlasManifest,
): AiSuggestOptions {
  const opKind = EDIT_OP_KIND[kind];
  const selectorKey = EDIT_SELECTOR_KEY[kind];
  const fields = EDIT_PATCHABLE_FIELDS[kind];
  const shape = `{"kind":"${opKind}","${selectorKey}":"${selectorId}",${fields
    .map((f) => `"${f}":string`)
    .join(',')}}`;
  return {
    prompt:
      `You are a senior schema architect proposing an edit to an existing ${kind} in this data model.\n\n` +
      `Current ${kind} (only include fields below that you are actually changing — do not repeat unchanged values):\n` +
      `${JSON.stringify(currentObject, null, 2)}\n\n` +
      `The user's instruction: "${instruction}"\n\n` +
      `Reply with ONLY a JSON array containing exactly one object matching this shape ` +
      `(only include the patch fields you are actually changing, omit the rest):\n${shape}\n\n` +
      `Never change "${selectorKey}" — it is a fixed identifier, not something this mechanism can rename. ` +
      `No prose, no markdown code fences — the JSON array is the entire response.`,
    summary: manifestSummary(manifest),
    knownEntityIds: allEntityIds(manifest),
  };
}

/** Well below CANDIDATE_THRESHOLD (0.4, matchFeatureFlags.ts) — this is deliberately a much looser floor,
 *  used only to give the AI prompt real (if weak) options instead of an empty list, never to auto-link. */
const WEAK_CANDIDATE_FLOOR = 0.15;
const WEAK_CANDIDATE_LIMIT = 5;

/** Score one fixed flag or Feature against every counterpart on the other side, keeping anything above
 *  WEAK_CANDIDATE_FLOOR — the low-floor sibling of manifest.flagFeatureCandidates (Task 4), computed
 *  on demand for the "Ask Atlas to link this" flow (spec §4: "even weak, sub-0.4" candidate context). */
export function weakCandidatesFor(
  side: 'feature' | 'flag',
  id: string,
  features: readonly Feature[],
  flagCatalogRows: readonly FlagCatalogRow[],
): MatchCandidate[] {
  const pairs: MatchCandidate[] =
    side === 'feature'
      ? (() => {
          const feature = features.find((f) => f.id === id);
          if (!feature) return [];
          return flagCatalogRows.map((flagRow) => ({
            featureId: feature.id,
            flagId: flagRow.flagId,
            ...scoreFeatureFlagPair(feature, flagRow, features, flagCatalogRows),
          }));
        })()
      : (() => {
          const flagRow = flagCatalogRows.find((f) => f.flagId === id);
          if (!flagRow) return [];
          return features.map((feature) => ({
            featureId: feature.id,
            flagId: flagRow.flagId,
            ...scoreFeatureFlagPair(feature, flagRow, features, flagCatalogRows),
          }));
        })();

  return pairs
    .filter((c) => c.score >= WEAK_CANDIDATE_FLOOR)
    .sort((a, b) => b.score - a.score)
    .slice(0, WEAK_CANDIDATE_LIMIT);
}

function candidateContextLines(candidates: MatchCandidate[]): string {
  if (candidates.length === 0) return '(no candidates scored above zero for this pair)';
  return candidates
    .map((c) => `- ${c.flagId} ↔ ${c.featureId} (weak signal: ${Math.round(c.score * 100)}%, matched on ${c.matchedOn.join(', ') || 'none'})`)
    .join('\n');
}

/** "Ask Atlas to link this" from the Feature-cluster side (spec §4's literal signature) — the model may
 *  legitimately respond with no op (reply "[]") when nothing is a confident match; the caller must treat
 *  that as a valid outcome, not an error (spec §4). */
export function proposeFeatureFlagLinkPrompt(
  feature: Feature,
  candidates: MatchCandidate[],
  flagCatalogRows: FlagCatalogRow[],
  manifest: AtlasManifest,
): AiSuggestOptions {
  return {
    prompt:
      `You are a senior schema architect. The Feature cluster "${feature.label}" (${feature.id}) has no ` +
      `linked feature flag. Below are the flags the matcher considered but scored too low to surface as a ` +
      `confident candidate:\n\n${candidateContextLines(candidates)}\n\n` +
      `All known flags:\n${flagCatalogRows.map((r) => `- ${r.flagId}: ${r.description || '(no description)'}`).join('\n')}\n\n` +
      `If one flag is clearly the right gate for this cluster, reply with ONLY a JSON array containing exactly ` +
      `one object: {"kind":"link-feature-flag","featureId":"${feature.id}","flagId":string}. ` +
      `If none of the flags is a confident match, reply with exactly: []. ` +
      `Do not invent a flagId that isn't listed above. No prose, no markdown code fences.`,
    summary: manifestSummary(manifest),
    knownEntityIds: allEntityIds(manifest),
  };
}

/** "Ask Atlas to link this" from the flag side — the direct mirror of proposeFeatureFlagLinkPrompt above,
 *  needed because Task 8 built the Ask Atlas affordance on both unlinked-flag and unlinked-cluster cards. */
export function proposeFlagFeatureLinkPrompt(
  flagRow: FlagCatalogRow,
  candidates: MatchCandidate[],
  features: Feature[],
  manifest: AtlasManifest,
): AiSuggestOptions {
  return {
    prompt:
      `You are a senior schema architect. The flag "${flagRow.flagId}" (${flagRow.description || 'no description'}) ` +
      `has no linked Feature cluster. Below are the clusters the matcher considered but scored too low to ` +
      `surface as a confident candidate:\n\n${candidateContextLines(candidates)}\n\n` +
      `All known Feature clusters:\n${features.map((f) => `- ${f.id}: ${f.label}`).join('\n')}\n\n` +
      `If one cluster is clearly the right owner for this flag, reply with ONLY a JSON array containing exactly ` +
      `one object: {"kind":"link-feature-flag","featureId":string,"flagId":"${flagRow.flagId}"}. ` +
      `If none of the clusters is a confident match, reply with exactly: []. ` +
      `Do not invent a featureId that isn't listed above. No prose, no markdown code fences.`,
    summary: manifestSummary(manifest),
    knownEntityIds: allEntityIds(manifest),
  };
}
